import { ref, set, ssrRef } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";
import Vue from "vue";

import useCreatorShopApi from "@/store/creator-shop-posts/-api";
import {
  CreatorShopPostsStore,
  PostSearchFeed,
  PostSearchFeeds,
  PostSearchHit,
  PostSearchMap,
  PostSearchMeta,
} from "@/store/creator-shop-posts/types";
import mapData from "@/utils/transform/map-data";

const DEFAULT_META = { nbHits: 0, nbPages: 0, page: 0 };
const DEFAULT_FETCH_STATE = { pending: false, error: null, timestamp: 0 };
const MAX_PINNED_POSTS = 3;
const emptyFeedFactory: () => PostSearchFeed = () => ({ ids: [], meta: DEFAULT_META });

export const useCreatorShopPostsStore = defineStore(
  "creator-shop-posts",
  () => {
    const term = ref<string | null>(null);
    const posts = ssrRef<PostSearchMap>({}, "posts-map");
    const totalNumberOfPosts = ssrRef<number>(0, "total-posts");
    const fetchState = ssrRef<Vue["$fetchState"]>(DEFAULT_FETCH_STATE, "fetch-state");
    const hasPinnedPosts = ssrRef<boolean>(false, "has-pinned-posts");
    const _shopFeeds = ssrRef<PostSearchFeeds>({}, "shop-feeds");
    const _searchFeeds = ref<PostSearchFeeds>({});

    const getFeed = (feed: string) => {
      const feedName = feed?.toLowerCase();
      return _shopFeeds.value?.[feedName]
        ? _shopFeeds.value?.[feedName]
        : _searchFeeds.value?.[feedName] ?? emptyFeedFactory();
    };

    const fetch: CreatorShopPostsStore["fetch"] = async function (profileId) {
      if (!profileId) {
        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
        return;
      }

      try {
        fetchState.value = { pending: true, error: null, timestamp: Date.now() };
        const meta = getFeed(profileId).meta;

        if (!feedHasMorePages(profileId)) {
          return false;
        }

        const response = await this.$api.get({
          query: "",
          profileId,
          ranking: "recent",
          page: meta.page < meta.nbPages ? meta.page + 1 : 0,
        });

        const { nbHits, nbPages, page } = response?.data || {};

        if (response.data.hits.length > 0) {
          const fetchedAt = Date.now();
          const posts = response.data.hits.map((post) => ({ ...post, fetchedAt }));
          const meta = { nbHits, nbPages, page };
          const filteredPosts = filterDuplicatePinnedPosts(posts);
          const mappedPosts = mapData("objectId", filteredPosts);
          const postIds = filteredPosts.map(({ objectId }) => objectId);

          totalNumberOfPosts.value = meta?.nbHits;
          setPosts({ mappedPosts });
          setFeed({ feed: profileId, ids: postIds, meta });
        }

        if (nbHits === 0 && nbPages === 0 && page === 0) {
          posts.value = {};
          setFeed({ feed: profileId, ids: [], meta: DEFAULT_META });
        }

        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
      } catch (error) {
        fetchState.value = { pending: false, error: error as Error, timestamp: Date.now() };
        throw error;
      }
    };

    const setFeed = ({ feed, ids, meta }: { feed: string; ids: string[]; meta: PostSearchMeta }) => {
      const feedName = feed?.toLowerCase();
      // if this is the first search set data, otherwise append data
      if (_shopFeeds.value?.[feedName] === undefined) {
        set(_shopFeeds.value, feedName, { ids, meta });
      } else {
        set(_shopFeeds.value, feedName, {
          ids: [..._shopFeeds.value[feedName].ids, ...ids],
          meta,
        });
      }
    };

    const setSearchFeed = ({ feed, ids, meta }: { feed: string; ids: string[]; meta: PostSearchMeta }) => {
      const feedName = feed?.toLowerCase();
      // clear out previous search results and shopfeed, if this is a new search
      if (_searchFeeds.value[feedName] === undefined) {
        _searchFeeds.value = {};
        _shopFeeds.value = {};
      }
      // if this is the first search set data, otherwise append data
      if (_searchFeeds.value?.[feedName] === undefined) {
        set(_searchFeeds.value, feedName, { ids, meta });
      } else {
        set(_searchFeeds.value, feedName, {
          ids: [..._searchFeeds.value[feedName].ids, ...ids],
          meta,
        });
      }
    };

    const setPosts = ({ mappedPosts }: { mappedPosts: PostSearchMap }) => {
      if (posts.value === undefined || Object.keys(posts.value).length === 0) {
        posts.value = mappedPosts;
      } else {
        posts.value = { ...posts.value, ...mappedPosts };
      }
    };

    const filterDuplicatePinnedPosts = (posts: PostSearchHit[]) => {
      // Backend retrieves posts and pins from 2 different services and concatenates the pins.
      // Pinned posts are returned twice and the duplicates must be removed
      const pinnedPosts = posts.slice(0, MAX_PINNED_POSTS).filter((amIPinned) => amIPinned.pinnedAt);

      hasPinnedPosts.value = pinnedPosts && pinnedPosts?.length !== 0;
      return posts.flatMap((result) => {
        // Check if this post is pinned
        const pinnedPost = pinnedPosts.find((pinnedPost: PostSearchHit) => result.objectId === pinnedPost.objectId);
        // Filter out pinned posts that do not have the pinnedAt data field.
        // Return all other posts in the data set
        if ((pinnedPost && result.pinnedAt) || !pinnedPost) {
          return result;
        } else {
          // Depending on the desired behavior, you might want to return null,
          // an empty object, or keep the original result
          // Here we return an empty object for consistency with the previous branch
          return [];
        }
      });
    };

    const getHeroAspectRatio = (postId: string): number => {
      const width = posts.value?.[postId]?.heroImageWidth || 1;
      const height = posts.value?.[postId]?.heroImageHeight || 1;
      return width / height;
    };

    const feedHasMorePages = (name: string) => {
      const feed = getFeed(name);
      // Results from the search endpoint are a 0 based page index
      return (feed.ids.length === 0 && feed.meta.nbPages === 0) || feed.meta.page <= feed.meta.nbPages - 1;
    };

    const fetchBySearchTerm: CreatorShopPostsStore["fetchBySearchTerm"] = async function (profileId: string) {
      if (!term.value || !profileId) {
        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
        return;
      }

      try {
        fetchState.value = { pending: true, error: null, timestamp: Date.now() };
        const meta = getFeed(term.value).meta;

        if (!feedHasMorePages(term.value)) {
          return false;
        }

        const response = await this.$api.get({
          query: term.value,
          profileId,
          ranking: "relevant",
          page: meta.page < meta.nbPages ? meta.page + 1 : 0,
        });

        const { nbHits, nbPages, page } = response?.data || {};

        if (response.data.hits.length > 0) {
          const fetchedAt = Date.now();
          const posts = response.data.hits.map((post) => ({ ...post, fetchedAt }));
          const meta = { nbHits, nbPages, page };
          const filteredPosts = filterDuplicatePinnedPosts(posts);
          const mappedPosts = mapData("objectId", filteredPosts);
          const postIds = filteredPosts.map(({ objectId }) => objectId);
          setPosts({ mappedPosts });
          setSearchFeed({ feed: term.value, ids: postIds, meta });
        }

        if (nbHits === 0 && nbPages === 0 && page === 0) {
          posts.value = {};
          setSearchFeed({ feed: term.value, ids: [], meta: DEFAULT_META });
        }

        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
      } catch (error) {
        fetchState.value = { pending: false, error: error as Error, timestamp: Date.now() };
        throw error;
      }
    };

    const $resetSearch = () => {
      term.value = null;
      _searchFeeds.value = {};
    };

    return {
      term,
      posts,
      totalNumberOfPosts,
      fetchState,
      _shopFeeds,
      _searchFeeds,
      fetch,
      fetchBySearchTerm,
      getFeed,
      getHeroAspectRatio,
      feedHasMorePages,
      hasPinnedPosts,
      $resetSearch,
    };
  },
  {
    api: useCreatorShopApi,
  },
);
